import { GridFileResponse } from "../fileGrid/cardFileGrid";
import { ImportQuoteFileResponse, QuoteImageResponse, QuoteProcurementPartInfoResponse } from "@/main/models/response";
import { isEmpty, isNullUndefined } from "@/main/services/utils";

export const IMAGE_EXTENSIONS = ["jpg", "jpeg", "png"];
export const EMPTY_VALUE = "--";

export function getFileExtension(fileName: string) {
	return fileName.slice(fileName.lastIndexOf(".") + 1).toLowerCase();
}

export function isImageFile(file: File) {
	return IMAGE_EXTENSIONS.includes(getFileExtension(file.name));
}

export function formatFileSize(sizeInBytes: number) {
	return `${Math.round(sizeInBytes / 1024)} KB`;
}

export function mapQuoteImageToGridFile(image: QuoteImageResponse): GridFileResponse {
	return {
		fileExtension: image.fileExtension,
		fileName: image.fileName,
		fileUrl: image.externalKey,
		guid: image.guid,
	};
}

function getFirstFilledValue(values: (string | undefined)[]) {
	return values.map((value) => value?.trim()).find((value) => !isEmpty(value));
}

export function getPartLabel(part: QuoteProcurementPartInfoResponse) {
	const partName = getFirstFilledValue([part.partName, part.supplierInventoryPartTitle, part.quoteItemDescription]);
	const partNumber = getFirstFilledValue([part.quoteItemPartNumber, part.quoteSupplierItemPartNumber]);

	if (isNullUndefined(partName)) return partNumber ?? EMPTY_VALUE;
	if (isNullUndefined(partNumber)) return partName;

	return `${partName} - ${partNumber}`;
}

const MIME_TYPE_BY_EXTENSION: Record<string, string> = {
	pdf: "application/pdf",
	json: "application/json",
	xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
	jpg: "image/jpeg",
	jpeg: "image/jpeg",
	png: "image/png",
};

export function getMimeTypeByExtension(fileExtension: string) {
	const extension = fileExtension.replace(".", "").toLowerCase();
	return MIME_TYPE_BY_EXTENSION[extension] ?? "application/octet-stream";
}

const BASE64_PREFIX_SEPARATOR = "base64,";

export function removeBase64Prefix(base64Data: string) {
	return base64Data.includes(BASE64_PREFIX_SEPARATOR) ? base64Data.split(BASE64_PREFIX_SEPARATOR)[1] : base64Data;
}

export interface PartAttachment extends QuoteImageResponse {
	isImage: boolean;
}

export function isImageExtension(fileExtension: string) {
	return IMAGE_EXTENSIONS.includes(fileExtension.replace(".", "").toLowerCase());
}

export function mapQuoteImageToPartAttachment(image: QuoteImageResponse): PartAttachment {
	return {
		...image,
		isImage: isImageExtension(image.fileExtension ?? ""),
	};
}

export function isRelatedToPart(file: ImportQuoteFileResponse) {
	return !isEmpty(file.quoteItemGuid);
}
